<script setup>
import { computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import { useBillingPlanCardContent } from 'dashboard/composables/useBillingPlanCardContent';
import { usePlanCardSeatPricing } from 'dashboard/composables/usePlanCardSeatPricing';

const props = defineProps({
  plan: { type: Object, required: true },
  duration: { type: Object, default: null },
  billingSuffix: { type: String, default: '/month' },
  subscribeLabel: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
  isHighlighted: { type: Boolean, default: false },
  isCurrentTrial: { type: Boolean, default: false },
  billingUnavailable: { type: Boolean, default: false },
  billingUnavailableMessage: { type: String, default: '' },
  appliedVoucher: { type: Object, default: null },
  agentsUsed: { type: Number, default: 0 },
  inboxesUsed: { type: Number, default: 0 },
});

const emit = defineEmits(['subscribe']);

const { t } = useI18n();
const planRef = toRef(props, 'plan');
const durationRef = toRef(props, 'duration');
const appliedVoucherRef = toRef(props, 'appliedVoucher');
const seatUsageRef = computed(() => ({
  agents: props.agentsUsed,
  inboxes: props.inboxesUsed,
}));
const { features, featureIntro } = useBillingPlanCardContent(planRef);

const {
  agentsCount,
  inboxesCount,
  baseAgents,
  baseInboxes,
  agentOptions,
  inboxOptions,
  priceLabel,
  originalPriceLabel,
  voucherApplies,
} = usePlanCardSeatPricing(
  planRef,
  durationRef,
  appliedVoucherRef,
  seatUsageRef
);

const displayName = computed(() => props.plan.name);
const displayDescription = computed(() => props.plan.description);

const showSeatSelectors = computed(
  () => baseAgents.value > 0 || baseInboxes.value > 0
);

const onSubscribeClick = () => {
  emit('subscribe', {
    plan: props.plan,
    agentsCount: agentsCount.value,
    inboxesCount: inboxesCount.value,
  });
};

const ctaLabel = computed(
  () =>
    props.subscribeLabel ||
    (props.isCurrentTrial
      ? t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.CURRENT_TRIAL')
      : t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.SUBSCRIBE_NOW'))
);

const currencySymbol = computed(
  () => props.duration?.currency_symbol || props.duration?.currency_code || ''
);

const formatExtraUnitPrice = amount => {
  if (amount == null || Number(amount) <= 0) return null;
  const formatted = Number(amount).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
  return `${currencySymbol.value}${formatted}`;
};

const agentsSeatHint = computed(() => {
  if (baseAgents.value <= 0) return null;

  const extraPrice = formatExtraUnitPrice(
    props.duration?.extra_agent_price_per_unit
  );

  if (!extraPrice) {
    return t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.AGENTS_INCLUDED_ONLY', {
      count: baseAgents.value,
    });
  }

  return t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.AGENTS_INCLUDED_EXTRA', {
    count: baseAgents.value,
    price: extraPrice,
    cycle: props.billingSuffix,
  });
});

const inboxesSeatHint = computed(() => {
  if (baseInboxes.value <= 0) return null;

  const extraPrice = formatExtraUnitPrice(
    props.duration?.extra_inbox_price_per_unit
  );

  if (!extraPrice) {
    return t(
      'GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.INBOXES_INCLUDED_ONLY',
      {
        count: baseInboxes.value,
      }
    );
  }

  return t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.INBOXES_INCLUDED_EXTRA', {
    count: baseInboxes.value,
    price: extraPrice,
    cycle: props.billingSuffix,
  });
});
</script>

<template>
  <article
    class="flex h-full min-w-0 w-full max-w-full flex-col rounded-xl border border-solid bg-n-surface-1 p-4 shadow-sm sm:p-6"
    :class="[
      isHighlighted ? 'border-n-brand ring-1 ring-n-brand' : 'border-n-weak',
      billingUnavailable && 'opacity-60',
    ]"
  >
    <div class="mb-4 flex flex-wrap items-start justify-between gap-2">
      <h2 class="mb-0 text-xl font-semibold text-n-slate-12">
        {{ displayName }}
      </h2>
      <span
        v-if="isCurrentTrial"
        class="rounded-md bg-n-brand/15 px-2 py-0.5 text-xs font-semibold text-n-brand"
      >
        {{ t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.CURRENT_TRIAL') }}
      </span>
    </div>

    <p
      v-if="billingUnavailable"
      class="mb-1 text-sm font-medium text-n-slate-11"
    >
      {{ billingUnavailableMessage }}
    </p>
    <div v-else-if="priceLabel" class="mb-1 flex flex-col gap-0.5">
      <p
        class="mb-0 break-words tabular-nums text-2xl font-semibold tracking-tight text-n-slate-12 sm:text-3xl"
      >
        <span
          v-if="originalPriceLabel"
          class="me-2 text-lg font-medium text-n-slate-11 line-through sm:text-xl"
        >
          {{ originalPriceLabel }}
        </span>
        <span :class="{ 'text-n-teal-11': originalPriceLabel }">
          {{ priceLabel }}
        </span>
        <span class="text-base font-medium text-n-slate-11">{{
          billingSuffix
        }}</span>
      </p>
      <p
        v-if="appliedVoucher && voucherApplies"
        class="mb-0 text-xs font-medium text-n-teal-11"
      >
        {{
          t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.VOUCHER_APPLIED', {
            code: appliedVoucher.code,
          })
        }}
      </p>
    </div>

    <p
      v-if="displayDescription"
      class="mb-4 text-sm leading-relaxed text-n-slate-11"
    >
      {{ displayDescription }}
    </p>

    <div
      v-if="showSeatSelectors"
      class="mb-4 rounded-lg border border-n-weak bg-n-slate-2 p-4"
    >
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label v-if="baseAgents > 0" class="flex min-w-0 flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.AGENTS_SELECT') }}
          </span>
          <Select
            v-model="agentsCount"
            :options="agentOptions"
            :visible-options="8"
            full-width
          />
          <p
            v-if="agentsSeatHint"
            class="mb-0 text-xs leading-relaxed text-n-slate-11"
          >
            {{ agentsSeatHint }}
          </p>
        </label>
        <label v-if="baseInboxes > 0" class="flex min-w-0 flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.INBOXES_SELECT') }}
          </span>
          <Select
            v-model="inboxesCount"
            :options="inboxOptions"
            :visible-options="8"
            full-width
          />
          <p
            v-if="inboxesSeatHint"
            class="mb-0 text-xs leading-relaxed text-n-slate-11"
          >
            {{ inboxesSeatHint }}
          </p>
        </label>
      </div>
    </div>

    <div v-if="features.length" class="mb-6 flex flex-1 flex-col gap-2">
      <p v-if="featureIntro" class="mb-0 text-sm font-semibold text-n-slate-12">
        {{ featureIntro }}
      </p>
      <ul class="mb-0 list-none space-y-2 ps-0">
        <li
          v-for="(feature, index) in features"
          :key="index"
          class="flex items-start gap-2 text-sm text-n-slate-11"
        >
          <span
            class="i-lucide-check mt-0.5 size-4 shrink-0 text-n-teal-10"
            aria-hidden="true"
          />
          <span class="min-w-0 break-words">{{ feature }}</span>
        </li>
      </ul>
    </div>

    <Button
      :label="ctaLabel"
      size="md"
      :is-loading="isLoading"
      :disabled="isCurrentTrial || billingUnavailable"
      class="mt-auto w-full"
      @click="onSubscribeClick"
    />
  </article>
</template>
